/**
 * Client-side entry point for Remix application (T175, T166).
 *
 * This module initializes the React application on the client side,
 * setting up Sentry for error tracking, accessibility live regions,
 * and hydrating the server-rendered content.
 *
 * Note: React Query providers are configured in root.tsx to avoid
 * hydration mismatches between server and client rendering.
 */

import { RemixBrowser } from '@remix-run/react';
import { startTransition, StrictMode, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';

import { initLiveRegion, destroyLiveRegion } from '~/lib/a11y';
import { initSentryClient } from '~/lib/sentry.client';

// Initialize Sentry before React hydration for early error capture
initSentryClient();

/**
 * Client-side application wrapper.
 * Initializes accessibility live regions for screen reader announcements.
 *
 * Note: QueryClientProvider is in root.tsx's Layout to ensure
 * consistent server/client rendering and avoid hydration errors.
 */
function ClientApp() {
  // Initialize accessibility live region for screen reader announcements
  useEffect(() => {
    initLiveRegion();
    return () => destroyLiveRegion();
  }, []);

  return (
    <StrictMode>
      <RemixBrowser />
    </StrictMode>
  );
}

// Use startTransition to hydrate for better performance
startTransition(() => {
  hydrateRoot(document, <ClientApp />);
});
